
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Form schema
const signinSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters long"),
});

const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters long"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  role: z.enum(["user", "instructor"], {
    errorMap: () => ({ message: "Please select an account type" }),
  }),
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: "You must accept the terms and conditions" }),
  }),
});

type SigninFormData = z.infer<typeof signinSchema>;
type SignupFormData = z.infer<typeof signupSchema>;
type AuthFormData = SigninFormData & Partial<SignupFormData>;

export function useAuthForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [showSpamAlert, setShowSpamAlert] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [signupAttempts, setSignupAttempts] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userState, setUserState] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, resetPassword } = useAuth();

  // Detect user location on component mount
  useEffect(() => {
    const detectUserLocation = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          setUserCountry(data.country || 'CA');
          setUserState(data.region_code || 'ON');
          console.log(`Detected location: ${data.country}, ${data.region_code}`);
        }
      } catch (error) {
        console.error("Error detecting location:", error);
        // Default to Canada, Ontario if detection fails
        setUserCountry('CA');
        setUserState('ON');
      }
    };
    
    detectUserLocation();
  }, []);

  // Set initial mode based on URL parameters
  useEffect(() => {
    const type = searchParams.get('type');
    console.log("URL type parameter:", type);
    
    // If we're in recovery mode, no need to change signin/signup mode
    if (type !== 'recovery') {
      // Default to signin mode
      setMode(searchParams.has('reset') ? "signin" : "signin");
    }
  }, [searchParams]);

  const form = useForm<AuthFormData>({
    resolver: zodResolver(mode === "signin" ? signinSchema : signupSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      role: undefined,
      acceptTerms: undefined,
    },
  });

  // Reset the form when the mode changes
  useEffect(() => {
    form.reset({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      role: undefined,
      acceptTerms: undefined,
    });
  }, [mode, form]);

  // Helper function to get default location IDs from detected country/state codes
  const getLocationIds = async () => {
    try {
      // Get country ID
      const { data: countryData } = await supabase
        .from('countries')
        .select('id')
        .eq('code', userCountry || 'CA')
        .single();
      
      if (!countryData) {
        // Default to Canada if country not found
        const { data: defaultCountry } = await supabase
          .from('countries')
          .select('id')
          .eq('code', 'CA')
          .single();
          
        return { countryId: defaultCountry?.id, stateId: null };
      }
      
      // Get state ID
      const { data: stateData } = await supabase
        .from('states')
        .select('id')
        .eq('code', userState || 'ON')
        .eq('country_id', countryData.id)
        .single();
      
      if (!stateData) {
        // Default to Ontario if state not found
        const { data: defaultState } = await supabase
          .from('states')
          .select('id')
          .eq('code', 'ON')
          .eq('country_id', countryData.id)
          .maybeSingle();
          
        return { countryId: countryData.id, stateId: defaultState?.id };
      }
      
      return { countryId: countryData.id, stateId: stateData.id };
    } catch (error) {
      console.error("Error getting location IDs:", error);
      return { countryId: null, stateId: null };
    }
  };

  const onSubmit = async (data: AuthFormData) => {
    setIsLoading(true);
    setShowSpamAlert(false);

    try {
      if (mode === "signin") {
        // Increment login attempts
        setLoginAttempts(prev => prev + 1);
        
        // Check if captcha should be shown and validated
        if (loginAttempts >= 2 && !isCaptchaValid) {
          setShowCaptcha(true);
          throw new Error("Please complete the CAPTCHA verification correctly");
        }
        
        const { error } = await signIn(data.email, data.password);

        if (error) {
          throw error;
        }

        // Reset login attempts on successful login
        setLoginAttempts(0);
        setShowCaptcha(false);
        toast.success("Welcome back!");
        navigate(searchParams.get("redirect") || "/dashboard");
      } else {
        // Increment signup attempts
        setSignupAttempts(prev => prev + 1);
        
        // Check if captcha should be shown and validated
        if (signupAttempts >= 1 && !isCaptchaValid) {
          setShowCaptcha(true);
          throw new Error("Please complete the CAPTCHA verification correctly");
        }
        
        if (!data.firstName || !data.lastName || !data.role) {
          throw new Error("First name, last name, and account type are required");
        }
        
        const { error } = await signUp(
          data.email, 
          data.password, 
          data.firstName, 
          data.lastName,
          data.acceptTerms || false,
          data.role
        );

        if (error) {
          throw error;
        }
        
        // Reset signup attempts on successful signup
        setSignupAttempts(0);
        setShowCaptcha(false);
        setShowSpamAlert(true);
        toast.success(
          "Check your email for a verification link. Please check your spam/junk folder if you don't see it in your inbox."
        );
        // No session yet — the user must confirm their email first.
        setMode("signin");
      }

    } catch (error: any) {
      console.error("Authentication error:", error);
      toast.error(error.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordReset = async (email: string) => {
    try {
      console.log("Requesting password reset for email:", email);
      const { error } = await resetPassword(email);
      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error("Password reset error:", error);
      throw error;
    }
  };

  const toggleMode = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    setShowSpamAlert(false);
    setShowCaptcha(false);
    form.reset();
  };
  
  const setCaptchaValid = (isValid: boolean) => {
    setIsCaptchaValid(isValid);
  };

  return {
    form,
    isLoading,
    mode,
    toggleMode,
    onSubmit,
    showSpamAlert,
    showCaptcha,
    setCaptchaValid,
    onPasswordReset,
    userCountry,
    userState
  };
}
